import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  Button,
  TableHead,
  IconButton,
  Switch,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { API_ENDPOINTS } from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";

const API_URL = API_ENDPOINTS.CATEGORIES;

const CategoriesListContent = () => {
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { user } = useAuth();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success' | 'error'
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(API_URL.BASE);
      const data = response.data;
      console.log(data);
      let categoriesArray = [];
      if (Array.isArray(data)) {
        categoriesArray = data;
      } else if (data && Array.isArray(data.categories)) {
        categoriesArray = data.categories;
      }
      setCategories(categoriesArray);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleDeleteClick = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = deleteDialog.id;
    setDeleteDialog({ open: false, id: null });
    try {
      await axios.delete(API_URL.DELETE(id));
      setCategories((prevCategories) => prevCategories.filter((category) => category.id !== id));
      setSnackbar({
        open: true,
        message: "Category deleted successfully!",
        severity: "success",
      });
    } catch (error) {
      let userMessage = "Failed to delete category.";
      if (
        error.response?.data?.message &&
        error.response.data.message.includes("foreign key constraint fails")
      ) {
        userMessage =
          "Cannot delete this category because it is assigned to other records. Please reassign or remove those records first.";
      }
      setSnackbar({
        open: true,
        message: userMessage,
        severity: "error",
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, id: null });
  };
  

  const handleTogglePublish = async (id, currentStatus) => {
    try {
      await axios.put(API_URL.PUBLISH(id), { publish: !currentStatus });
      setCategories((prevCategories) =>
        prevCategories.map((category) =>
          category.id === id ? { ...category, publish: !currentStatus } : category
        )
      );
    } catch (error) {
      console.error("Error updating publish status:", error);
    }
  };
  

  const filteredCategories = Array.isArray(categories)
    ? categories.filter((category) =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <Card sx={{ boxShadow: "none", borderRadius: "7px", mb: "25px", padding: "25px" }}>
      {/* <NavList /> */}
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: "25px" }}>
          <input
            type="text"
            placeholder="Search category type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: "8px", width: "200px", borderRadius: "5px", border: "1px solid #ccc" }}
          />
          {user.permissions.includes("add-categories") && (
            <Link to="/ecommerce/create-categories">
              <Button variant="outlined" color="primary">
                <AddIcon /> Add New Category
              </Button>
            </Link>
          )}
        </Box>

        <TableContainer className="custom-table" component={Paper}>
          <Table sx={{ minWidth: 1000 }} aria-label="User Types Table">
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Publish</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCategories
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((category, index) => (
                  <TableRow key={category.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{category.name}</TableCell>
                    <TableCell>
                      <Switch
                        checked={category.publish}
                        onChange={() => handleTogglePublish(category.id, category.publish)}
                        color="success"
                      />
                    </TableCell>
                    <TableCell>
                      {user.permissions.includes("view-categories") && (
                        <Link to={`/ecommerce/categories-list/details/${category.id}`}>
                          <IconButton color="info">
                            <VisibilityIcon />
                          </IconButton>
                        </Link>
                      )}
                      {user.permissions.includes("edit-categories") && (
                        <Link to={`/ecommerce/edit-categories/${category.id}`}>
                          <IconButton color="primary">
                            <EditIcon />
                          </IconButton>
                        </Link>
                      )}
                      {user.permissions.includes("delete-categories") && (
                        <IconButton color="error" onClick={() => handleDeleteClick(category.id)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  count={filteredCategories.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this category?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={1500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default CategoriesListContent;